import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  TextField,
  Divider,
} from "@mui/material";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { assignVouchersToMerchant, AssignVouchersRequest } from "../../../../services/merchant/merchant_actions";
import { Merchant } from "../../model/merchant_model";
import { purple, purpleLight } from "../../../../theme/colors";

interface AssignVouchersDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedMerchant: Merchant | null;
}

export default function AssignVouchersDialog({
  open,
  onClose,
  onSuccess,
  selectedMerchant,
}: AssignVouchersDialogProps) {
  const [numberOfVouchers, setNumberOfVouchers] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useFeedback();

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setNumberOfVouchers(1);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedMerchant) {
      showSnackbar("No merchant selected");
      return;
    }
    if (numberOfVouchers <= 0) {
      showSnackbar("Please enter a valid number of vouchers");
      return;
    }

    setIsLoading(true);
    try {
      const request: AssignVouchersRequest = {
        merchant_id: selectedMerchant.id,
        number_of_vouchers: numberOfVouchers,
      };
      
      await assignVouchersToMerchant(request);
      showSnackbar(`${numberOfVouchers} voucher(s) assigned to ${selectedMerchant.name} successfully!`);
      onSuccess();
      onClose();
    } catch (error) {
      showSnackbar("Failed to assign vouchers");
      console.error("Error assigning vouchers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form
      setNumberOfVouchers(1);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="600">
          Assign Vouchers to Merchant
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Selected Merchant Info */}
            {selectedMerchant && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: purpleLight, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Assigning Vouchers to:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedMerchant.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Location:</strong> {selectedMerchant.location}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Pincode:</strong> {selectedMerchant.pincode}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Mobile:</strong> {selectedMerchant.mobile_no || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {selectedMerchant.status || "N/A"}
                  </Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Number of Vouchers Input */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Number of Vouchers to Assign"
                type="number"
                value={numberOfVouchers}
                onChange={(e) => setNumberOfVouchers(Number(e.target.value))}
                placeholder="Enter number of vouchers"
                disabled={isLoading}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !selectedMerchant || numberOfVouchers <= 0}
          sx={{ backgroundColor: purple }}
        >
          {isLoading ? "Assigning..." : `Assign ${numberOfVouchers} Voucher(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
